import type { InvestorDocument } from '@/types/investor';

type LinkedDocumentLabelSource = Pick<
  InvestorDocument,
  'displayName' | 'category' | 'originalFileName' | 'subType'
>;

const trimmed = (value: string | null | undefined): string => value?.trim() ?? '';

const UUID_STEM_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const resolveOriginalDocumentFileName = (
  document: Pick<InvestorDocument, 'originalFileName'>
): string => {
  const pathSegments = trimmed(document.originalFileName)
    .split(/[\\/]/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  return pathSegments[pathSegments.length - 1] ?? '';
};

const withoutFinalExtension = (fileName: string): string => {
  const finalDotIndex = fileName.lastIndexOf('.');
  return finalDotIndex > 0
    ? fileName.slice(0, finalDotIndex).trim()
    : fileName;
};

const resolveDocumentFileStem = (document: LinkedDocumentLabelSource): string => {
  const originalFileName = resolveOriginalDocumentFileName(document);
  return withoutFinalExtension(originalFileName);
};

const resolveCustomDisplayName = (document: LinkedDocumentLabelSource): string =>
  trimmed(document.displayName);

const hasOpaqueFileStem = (fileStem: string): boolean => UUID_STEM_PATTERN.test(fileStem);

/**
 * Resolve the human-readable identity shown in Processing/Closing Linked Documents.
 * A stored display name is always an intentional custom title. When no custom title
 * exists, preserve subtype context on these surfaces before falling back to the file.
 */
export const resolveLinkedDocumentLabel = (document: LinkedDocumentLabelSource): string => {
  const displayName = resolveCustomDisplayName(document);
  const category = trimmed(document.category);
  const subType = trimmed(document.subType);
  const fileStem = resolveDocumentFileStem(document);

  if (displayName) {
    return displayName;
  }

  if (subType) {
    return category ? `${category} - ${subType}` : subType;
  }

  if (!hasOpaqueFileStem(fileStem) && fileStem) {
    return fileStem;
  }

  return category || 'Document';
};

/**
 * Resolve the file-first identity shown in the Documents tab. A nonblank stored
 * display name is authoritative; NULL means the file name should be used. Imported
 * UUID filenames fall back to subtype/category so implementation ids are never shown.
 */
export const resolveInvestorDocumentLabel = (document: LinkedDocumentLabelSource): string => {
  const displayName = resolveCustomDisplayName(document);
  const category = trimmed(document.category);
  const subType = trimmed(document.subType);
  const fileStem = resolveDocumentFileStem(document);

  if (displayName) {
    return displayName;
  }

  if (!hasOpaqueFileStem(fileStem) && fileStem) {
    return fileStem;
  }

  return subType || category || 'Document';
};
